import type {
  ReimputeRef,
  TimeDay,
  TimeDayBlock,
  UnassignedActivity,
} from "../api/types";

export type AutoKind = "rule" | "history" | "neighbor";

/** Motif posé sur la réimputation : TAWA le relit et rend `auto_imputation`
 * sur le bloc. Ces libellés sont le contrat, au caractère près. */
export const AUTO_REASONS: Record<AutoKind, string> = {
  rule: "rattachement automatique : règle",
  history: "rattachement automatique : historique",
  neighbor: "rattachement automatique : voisinage",
};

export const AUTO_LABELS: Record<AutoKind, string> = {
  rule: "règle",
  history: "historique",
  neighbor: "voisinage",
};

/** Écart maximal, de chaque côté, entre un créneau et les blocs du projet qui
 * l'encadrent. */
export const NEIGHBOR_GAP_MIN = 30;
/** Sous ce cumul par jour, le temps sans projet n'est plus réclamé. */
export const NEGLIGIBLE_DAY_MIN = 5;
const MAX_BATCH = 200;

export interface AutoAttachItem extends ReimputeRef {
  event_id: string;
}

export interface AutoAttachBatch {
  kind: AutoKind;
  date: string;
  minutes: number;
  items: AutoAttachItem[];
}

function neighborRef(
  blocks: TimeDayBlock[],
  eventId: string,
): ReimputeRef | null {
  const slot = blocks.find((b) => b.start_event_id === eventId);
  if (!slot || slot.open || slot.start_minute <= 0 || slot.end_minute >= 1440) {
    return null;
  }
  const anchors = blocks.filter(
    (b) => b.project_id && b.start_event_id !== eventId,
  );
  const before = anchors
    .filter((b) => b.end_minute <= slot.start_minute)
    .sort((x, y) => y.end_minute - x.end_minute)[0];
  const after = anchors
    .filter((b) => b.start_minute >= slot.end_minute)
    .sort((x, y) => x.start_minute - y.start_minute)[0];
  if (!before || !after || before.project_id !== after.project_id) return null;
  if (
    slot.start_minute - before.end_minute > NEIGHBOR_GAP_MIN ||
    after.start_minute - slot.end_minute > NEIGHBOR_GAP_MIN
  ) {
    return null;
  }
  // Un voisin lui-même rattaché par voisinage ne propage pas : pas de chaîne.
  if (
    before.auto_imputation === "neighbor" ||
    after.auto_imputation === "neighbor"
  ) {
    return null;
  }
  const label = after.project_label || before.project_label;
  return {
    project_id: before.project_id!,
    ...(label ? { project_label: label } : {}),
  };
}

/**
 * Ce que Chronos rattache de lui-même, dans l'ordre : la suggestion d'une
 * règle, celle de l'historique, puis le projet qui encadre le créneau le même
 * jour. Seul le temps encore sans projet est lu : un créneau déjà rattaché,
 * par le salarié ou non, n'y figure pas. `skip` écarte les créneaux déjà
 * tentés. Un créneau ouvert, au projet invalide ou à cheval sur deux jours
 * reste au salarié.
 */
export function planAutoAttach(
  activities: UnassignedActivity[],
  days: Iterable<TimeDay>,
  skip: ReadonlySet<string> = new Set(),
): AutoAttachBatch[] {
  const blocksByDay = new Map<string, TimeDayBlock[]>();
  for (const d of days) blocksByDay.set(d.date, d.blocks);
  const pieces = new Map<string, number>();
  for (const a of activities) {
    pieces.set(a.event_id, (pieces.get(a.event_id) ?? 0) + 1);
  }

  const batches = new Map<string, AutoAttachBatch>();
  const seen = new Set<string>();
  for (const a of activities) {
    if (seen.has(a.event_id) || skip.has(a.event_id)) continue;
    if (a.open || a.invalid_reason) continue;
    let kind: AutoKind;
    let ref: ReimputeRef | null;
    const sp = a.suggested_project;
    if (sp) {
      kind = sp.source;
      ref = {
        project_id: sp.project_id,
        ...(sp.work_item_id ? { work_item_id: sp.work_item_id } : {}),
        ...(sp.project_label ? { project_label: sp.project_label } : {}),
      };
    } else {
      if ((pieces.get(a.event_id) ?? 0) > 1) continue;
      kind = "neighbor";
      ref = neighborRef(blocksByDay.get(a.date) ?? [], a.event_id);
    }
    if (!ref) continue;
    seen.add(a.event_id);
    const key = `${kind}|${a.date}`;
    const b = batches.get(key) ?? { kind, date: a.date, minutes: 0, items: [] };
    if (b.items.length >= MAX_BATCH) continue;
    b.items.push({
      event_id: a.event_id,
      ...ref,
      ...(ref.project_label
        ? { project_label: ref.project_label.slice(0, 200) }
        : {}),
    });
    b.minutes += a.worked_minutes;
    batches.set(key, b);
  }
  const order: AutoKind[] = ["rule", "history", "neighbor"];
  return [...batches.values()].sort(
    (x, y) =>
      order.indexOf(x.kind) - order.indexOf(y.kind) ||
      x.date.localeCompare(y.date),
  );
}

/** Temps sans projet encore réclamé : un jour qui en cumule moins de
 * NEGLIGIBLE_DAY_MIN n'en a plus. */
export function claimableActivities(
  activities: UnassignedActivity[],
): UnassignedActivity[] {
  const byDay = new Map<string, number>();
  for (const a of activities) {
    byDay.set(a.date, (byDay.get(a.date) ?? 0) + a.worked_minutes);
  }
  return activities.filter(
    (a) => (byDay.get(a.date) ?? 0) >= NEGLIGIBLE_DAY_MIN,
  );
}

export function claimableMinutes(activities: UnassignedActivity[]): number {
  return claimableActivities(activities).reduce(
    (sum, a) => sum + a.worked_minutes,
    0,
  );
}

/** UUID stable d'un texte : le même lot rejoué porte la même clé
 * d'idempotence, TAWA le reconnaît au lieu de réimputer deux fois. */
export function stableUuid(text: string): string {
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;
  for (let i = 0; i < text.length; i++) {
    const k = text.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  const hex = [h1 ^ h2 ^ h3 ^ h4, h2 ^ h1, h3 ^ h1, h4 ^ h1]
    .map((h) => (h >>> 0).toString(16).padStart(8, "0"))
    .join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `5${hex.slice(13, 16)}`,
    `${((parseInt(hex[16]!, 16) & 0x3) | 0x8).toString(16)}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join("-");
}

export function batchCorrelationId(batch: AutoAttachBatch): string {
  const items = [...batch.items]
    .sort((x, y) => x.event_id.localeCompare(y.event_id))
    .map((i) => `${i.event_id}>${i.project_id}:${i.work_item_id ?? ""}`)
    .join(",");
  return stableUuid(`${AUTO_REASONS[batch.kind]}|${items}`);
}
